/**
 * MarketWatchTicker (Jul-2026, User-Wunsch) — Themen-Newsticker unter dem
 * Social-Media-Studio, gefiltert auf die Hauptmarken des Händlers.
 * Nur das THEMA ist auswählbar — die KI arbeitet es eigenständig aus und
 * prüft vor Übernahme das Urheberrechts-Risiko (Ampel).
 */
import { useEffect, useState } from "react";
import { Radar, Loader2, Sparkles, X, ShieldCheck, ShieldAlert, ShieldX, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const RISK = {
    green: { label: "Urheberrecht: GRÜN — unbedenklich", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: ShieldCheck },
    yellow: { label: "Urheberrecht: GELB — bitte prüfen", cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: ShieldAlert },
    red: { label: "Urheberrecht: ROT — Veröffentlichung blockiert", cls: "bg-red-100 text-red-800 border-red-300", Icon: ShieldX },
};

function TopicModal({ topic, onClose }) {
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [transferring, setTransferring] = useState(false);

    const generate = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/dealer/market-watch/topics/${topic.id}/draft`);
            setDraft(r.data.draft);
        } catch (e) { toast.error(e?.response?.data?.detail || "KI-Ausarbeitung fehlgeschlagen"); }
        finally { setBusy(false); }
    };
    const toSocial = async () => {
        setTransferring(true);
        try {
            await api.post(`/dealer/market-watch/drafts/${draft.id}/to-social`);
            toast.success("Als Entwurf ins Social Studio übernommen — dort Bild wählen & veröffentlichen.");
            onClose(true);
        } catch (e) { toast.error(e?.response?.data?.detail || "Übernahme fehlgeschlagen"); }
        finally { setTransferring(false); }
    };

    const check = draft?.copyright_check || {};
    const risk = RISK[check.risk] || RISK.yellow;
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="mw-topic-modal">
            <div className="bg-white rounded-sm w-full max-w-xl mt-12 mb-12 shadow-2xl p-5">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400">{topic.source_label}</div>
                        <h3 className="text-lg font-black text-[#111827]">{topic.title}</h3>
                        <p className="text-xs text-gray-500 mt-1">{topic.summary}</p>
                    </div>
                    <button onClick={() => onClose(false)} className="text-gray-400 hover:text-[#111827]" data-testid="mw-topic-close"><X className="w-5 h-5" /></button>
                </div>
                {!draft ? (
                    <div className="mt-4">
                        <p className="text-[11px] text-gray-500 mb-3">
                            Aus urheberrechtlichen Gründen wird nur das <b>Thema</b> übernommen: Die KI schreibt einen komplett eigenen Text
                            und prüft das Ergebnis anschließend auf Urheberrechts-Risiken, bevor etwas veröffentlicht werden kann.
                        </p>
                        <button onClick={generate} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#111827] text-white rounded-sm px-4 py-2.5 text-sm font-bold hover:bg-black disabled:opacity-60" data-testid="mw-generate-draft">
                            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> KI arbeitet das Thema aus…</> : <><Sparkles className="w-4 h-4" /> Thema von der KI ausarbeiten lassen</>}
                        </button>
                    </div>
                ) : (
                    <div className="mt-4 space-y-3" data-testid="mw-draft-result">
                        <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-sm whitespace-pre-wrap" data-testid="mw-draft-text">{draft.text}</div>
                        {draft.hashtags?.length > 0 && (
                            <div className="text-xs text-sky-700">{draft.hashtags.map((h) => `#${h}`).join(" ")}</div>
                        )}
                        <div className={`border rounded-sm px-3 py-2 ${risk.cls}`} data-testid="mw-copyright-check">
                            <div className="flex items-center gap-2 text-sm font-bold"><risk.Icon className="w-4 h-4" /> {risk.label}</div>
                            {(check.reasons || []).length > 0 && (
                                <ul className="mt-1 text-xs list-disc ml-5">{check.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                            )}
                            {check.recommendation && <div className="text-xs mt-1 italic">{check.recommendation}</div>}
                        </div>
                        <div className="flex gap-2">
                            <button onClick={generate} disabled={busy} className="flex-1 border border-gray-300 rounded-sm px-3 py-2 text-sm font-bold hover:bg-gray-50" data-testid="mw-regenerate">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Neu ausarbeiten"}
                            </button>
                            <button onClick={toSocial} disabled={transferring || check.risk === "red"} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-600 text-white rounded-sm px-3 py-2 text-sm font-bold hover:bg-emerald-700 disabled:opacity-50" data-testid="mw-to-social"
                                title={check.risk === "red" ? "Blockiert: Urheberrechts-Risiko ROT" : "Als Entwurf ins Social Studio übernehmen"}>
                                {transferring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Ins Social Studio
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// Jul-2026 (Phase 2): Branchen-Umfrage — Händler stimmen direkt im Ticker ab.
function SurveyCard({ survey, onVoted }) {
    const [busy, setBusy] = useState(false);
    const voted = survey.my_vote !== null && survey.my_vote !== undefined;
    const vote = async (idx) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/dealer/market-watch/surveys/${survey.id}/vote`, { option_index: idx });
            toast.success("Danke für deine Stimme!");
            onVoted(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Abstimmen fehlgeschlagen"); }
        setBusy(false);
    };
    return (
        <div className="mt-3 bg-white/5 border border-white/10 rounded-sm p-3" data-testid={`mw-survey-card-${survey.id}`}>
            <div className="text-[9px] font-black uppercase tracking-wider text-[#FFCC00] mb-1">Branchen-Umfrage</div>
            <div className="text-xs font-bold text-white mb-2">{survey.question}</div>
            <div className="flex flex-wrap gap-1.5">
                {survey.options.map((o, idx) => {
                    const pct = survey.total_votes ? Math.round(((survey.votes?.[idx] || 0) / survey.total_votes) * 100) : 0;
                    return voted ? (
                        <span key={idx} className={`text-[11px] px-2 py-1 rounded-sm border ${survey.my_vote === idx ? "border-[#FFCC00] text-[#FFCC00] font-bold" : "border-white/20 text-gray-300"}`} data-testid={`mw-survey-result-${survey.id}-${idx}`}>
                            {o} · {pct}%
                        </span>
                    ) : (
                        <button key={idx} type="button" disabled={busy} onClick={() => vote(idx)}
                            className="text-[11px] px-2 py-1 rounded-sm border border-white/20 text-white hover:border-[#FFCC00] hover:text-[#FFCC00] disabled:opacity-50"
                            data-testid={`mw-survey-vote-${survey.id}-${idx}`}>
                            {o}
                        </button>
                    );
                })}
            </div>
            {voted && <div className="text-[10px] text-gray-400 mt-1">{survey.total_votes} Stimme{survey.total_votes !== 1 ? "n" : ""} — danke fürs Mitmachen!</div>}
        </div>
    );
}

export default function MarketWatchTicker() {
    const [items, setItems] = useState(null);
    const [selected, setSelected] = useState(null);
    const [surveys, setSurveys] = useState([]);

    useEffect(() => {
        api.get("/dealer/market-watch/ticker").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
        api.get("/dealer/market-watch/surveys").then((r) => setSurveys(r.data.items || [])).catch(() => {});
    }, []);

    if ((items === null || items.length === 0) && surveys.length === 0) return null;
    return (
        <div className="mt-8 bg-[#111827] rounded-sm p-4" data-testid="market-watch-ticker">
            <div className="flex items-center gap-2 mb-3">
                <Radar className="w-4 h-4 text-[#FFCC00]" />
                <span className="text-xs font-black uppercase tracking-wider text-[#FFCC00]">Themen-Ticker · Marktbeobachtung</span>
                <span className="text-[10px] text-gray-400">— auf deine Hauptmarken gefiltert · Thema anklicken, die KI arbeitet es rechtssicher aus</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
                {(items || []).map((t) => (
                    <button key={t.id} onClick={() => setSelected(t)}
                        className="shrink-0 max-w-[280px] text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm px-3 py-2 transition-colors"
                        data-testid={`mw-ticker-topic-${t.id}`}>
                        <div className="flex items-center gap-1.5">
                            <span className="text-[9px] font-mono-num font-bold px-1 py-0.5 rounded-sm bg-[#FFCC00] text-[#111827]" title="Reichweiten-Score">{t.reach_score}</span>
                            {t.reach_score >= 70 && <span className="text-[9px] uppercase font-bold text-emerald-400">KI-Tipp</span>}
                            <span className="text-[9px] uppercase text-gray-400">{(t.brands || []).slice(0, 2).join(", ") || "Allgemein"}</span>
                        </div>
                        <div className="text-xs font-bold text-white mt-1 line-clamp-2">{t.title}</div>
                    </button>
                ))}
            </div>
            {surveys.map((s) => (
                <SurveyCard key={s.id} survey={s} onVoted={(upd) => setSurveys((prev) => prev.map((x) => x.id === upd.id ? upd : x))} />
            ))}
            {selected && <TopicModal topic={selected} onClose={() => setSelected(null)} />}
        </div>
    );
}
